/**
 * ActivityTab — activity timeline with newest-first ordering and expandable detail view.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 *
 * Changes from original (US-74):
 * 1. Sort order REVERSED: b.createdAt.localeCompare(a.createdAt) (newest first).
 *    Original was a.createdAt.localeCompare(b.createdAt) (oldest first).
 * 2. Each event row gains a toggle button for expandable detail view.
 *    Expanded state shows all payload keys as label/value rows.
 *
 * Activity data comes from lead.events (in fetchLead response) — no new useQuery needed.
 */

import { useState } from 'react';
import type { LeadEvent } from './shared';
import { ACTIVITY_LABELS, formatActivityDetail, getPayloadField, formatDateTime } from './shared-constants';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ActivityTabProps {
  events: LeadEvent[];
}

// ---------------------------------------------------------------------------
// renderPayloadRows helper
// ---------------------------------------------------------------------------

function renderPayloadRows(payload: unknown): React.ReactNode {
  if (!payload || typeof payload !== 'object') return null;
  const entries = Object.entries(payload as Record<string, unknown>)
    .filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return null;
  return (
    <dl className="mt-2 space-y-1 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <dt className="text-muted-foreground capitalize min-w-24 shrink-0">
            {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
          </dt>
          <dd className="text-foreground break-all">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// ActivityTimeline (local)
// ---------------------------------------------------------------------------

function ActivityTimeline({ events }: ActivityTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // US-74: newest-first ordering (intentional behavior change from oldest-first)
  const sorted = [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <section data-testid="lead-activity-timeline" className="mb-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-base font-semibold mb-4">Activity</h2>

      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No activity recorded yet.</p>
      ) : (
        <ol className="space-y-3">
          {sorted.map((event) => {
            const detail = formatActivityDetail(event.eventType, event.payload);
            const actorName = getPayloadField(event.payload, 'actorName');
            const expanded = expandedId === event.id;

            return (
              <li
                key={event.id}
                data-testid={`activity-${event.id}`}
                className="flex items-start gap-3 text-sm"
              >
                {/* Expand/collapse toggle button (replaces the static dot) */}
                <button
                  type="button"
                  data-testid={`activity-expand-${event.id}`}
                  onClick={() => setExpandedId((id) => id === event.id ? null : event.id)}
                  aria-expanded={expanded}
                  aria-controls={`activity-detail-${event.id}`}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} details`}
                  className="mt-1.5 h-2 w-2 rounded-full bg-muted-foreground shrink-0 cursor-pointer hover:bg-foreground transition-colors"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {ACTIVITY_LABELS[event.eventType] ?? event.eventType.replace(/[._]/g, ' ')}
                  </p>
                  {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
                  {actorName && <p className="text-xs text-muted-foreground">by {actorName}</p>}
                  <p className="text-xs text-muted-foreground">{formatDateTime(event.createdAt)}</p>
                  {expanded && (
                    <div
                      id={`activity-detail-${event.id}`}
                      data-testid={`activity-detail-panel-${event.id}`}
                      className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2"
                    >
                      {renderPayloadRows(event.payload)}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ActivityTab
// ---------------------------------------------------------------------------

export function ActivityTab({ events }: ActivityTabProps) {
  return (
    <div>
      <ActivityTimeline events={events} />
    </div>
  );
}
